import { forwardRef } from "react";
import { cn } from "@/lib/cn";
import { Input, type InputProps } from "./Input";

export interface NumberInputProps
  extends Omit<InputProps, "onChange" | "value" | "type" | "leftSection" | "rightSection"> {
  value?: number | "";
  onChange?: (value: number | "") => void;
  min?: number;
  max?: number;
  step?: number;
  /** Texto fijo antes del valor (ej. `"$"`). */
  prefix?: string;
  /** Texto fijo después del valor (ej. `"%"`). */
  suffix?: string;
  /**
   * Redondea al salir del campo (`onBlur`) a N decimales · reemplazo puntual
   * de `decimalScale`+`fixedDecimalScale` de Mantine (que formateaban en
   * vivo). Acá el redondeo ocurre al perder el foco, no en cada tecleo, para
   * no pelear con el cursor mientras el usuario escribe.
   */
  decimals?: number;
  /** Oculta las flechitas nativas del `input[type=number]` (mismo look que `hideControls` de Mantine). */
  hideControls?: boolean;
}

/**
 * Reemplazo de `<NumberInput>` de Mantine · input nativo `type="number"` +
 * `prefix`/`suffix` como texto fijo (sin el formato de miles en vivo que
 * hace Mantine — si algún call-site lo necesita, se resuelve puntualmente
 * ahí con `Intl.NumberFormat`, no en el átomo).
 */
export const NumberInput = forwardRef<HTMLInputElement, NumberInputProps>(
  ({ value, onChange, prefix, suffix, decimals, hideControls, className, onBlur, ...props }, ref) => (
    <Input
      ref={ref}
      type="number"
      value={value}
      onChange={(e) => {
        const raw = e.currentTarget.value;
        onChange?.(raw === "" ? "" : Number(raw));
      }}
      onBlur={(e) => {
        if (decimals !== undefined && value !== "" && value !== undefined) {
          const redondeado = Number(value.toFixed(decimals));
          if (redondeado !== value) onChange?.(redondeado);
        }
        onBlur?.(e);
      }}
      leftSection={prefix}
      rightSection={suffix}
      className={cn(hideControls && "[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none", className)}
      {...props}
    />
  )
);
NumberInput.displayName = "NumberInput";
